<template>
  <div class="GenerateInvoice-dialog Transfer Card Card--top">
    <div class="Card-header">
      <button 
        class="Card-headerControl Button Transfer-close" 
        @click="$emit('close', true)">
        &times;
      </button>
      <h2 class="Card-title">Generate Invoice</h2>
    </div>
    <div class="Card-body">
      <form @submit.prevent="generateInvoice">
        <!-- Pre-filled User Details (Read-only) -->
        <div class="Form-inputGroup">
          <label class="Form-label">User (Organiser)</label>
          <div class="GenerateInvoice-userDisplay">
            <p class="GenerateInvoice-userName">
              <strong>{{ userDisplayName }}</strong>
            </p>
            <p class="GenerateInvoice-userEmail">{{ userEmail }}</p>
          </div>
          <input type="hidden" v-model="form.user_id" />
        </div>

        <!-- Date Range -->
        <div class="GenerateInvoice-dateRange">
          <div class="Form-inputGroup" :class="{ 'Form-inputGroup--error': !form.date_from && formSubmitted }">
            <label class="Form-label" for="date_from">From Date *</label>
            <input
              id="date_from"
              type="date"
              v-model="form.date_from"
              class="Input-field"
              :max="maxDate"
              required
            />
            <p v-if="!form.date_from && formSubmitted" class="Form-error">
              From date is required
            </p>
          </div>

          <div class="Form-inputGroup" :class="{ 'Form-inputGroup--error': !form.date_to && formSubmitted }">
            <label class="Form-label" for="date_to">To Date *</label>
            <input
              id="date_to"
              type="date"
              v-model="form.date_to"
              class="Input-field"
              :min="form.date_from"
              :max="maxDate"
              required
            />
            <p v-if="!form.date_to && formSubmitted" class="Form-error">
              To date is required
            </p>
          </div>

          <p v-if="dateRangeError" class="Form-error">
            {{ dateRangeError }}
          </p>
        </div>

        <!-- Auto-Send Option -->
        <div class="Form-inputGroup">
          <label>
            <input
              type="checkbox"
              name="auto_send"
              v-model="form.auto_send"
            />
            Auto-send invoice via email
          </label>
          <p class="Form-helpText">
            If checked, the invoice will be sent immediately to the user's email address after generation.
            If unchecked, the invoice will be created as a draft.
          </p>
        </div>

        <!-- Submit Button -->
        <button
          class="Button"
          type="submit"
          :disabled="isDisabled || isSubmitting"
        >
          <span v-if="isSubmitting">Generating...</span>
          <span v-else>Generate Invoice</span>
        </button>

        <!-- Error Message -->
        <p v-if="asyncFormError" class="Error">
          {{ asyncFormError }}
        </p>

        <!-- Success Message & Invoice Summary -->
        <div v-if="successMessage" class="GenerateInvoice-success">
          <p class="GenerateInvoice-successMessage">{{ successMessage }}</p>

          <div v-if="generatedInvoice && generatedInvoice.is_duplicate" class="GenerateInvoice-duplicateWarning">
            <p>An invoice with identical data already exists. The existing invoice is shown below.</p>
          </div>

          <div v-if="generatedInvoice" class="GenerateInvoice-summary">
            <p class="GenerateInvoice-invoiceDetails">
              Invoice Reference: <strong>{{ generatedInvoice.invoice_reference }}</strong><br>
              Invoice ID: <strong>{{ generatedInvoice.invoice_id }}</strong><br>
              Status: <strong>{{ generatedInvoice.status }}</strong>
            </p>
            <div v-if="hasTotals" class="GenerateInvoice-totals">
              <p><span class="GenerateInvoice-totalsLabel">Gross:</span> {{ formatCurrency(generatedInvoice.total_gross) }}</p>
              <p><span class="GenerateInvoice-totalsLabel">Fees:</span> {{ formatCurrency(generatedInvoice.total_fees) }}</p>
              <p><span class="GenerateInvoice-totalsLabel">Net:</span> {{ formatCurrency(generatedInvoice.total_net) }}</p>
            </div>
            <p v-if="generatedInvoice.items_count != null" class="GenerateInvoice-itemsCount">
              Items: <strong>{{ generatedInvoice.items_count }}</strong> {{ generatedInvoice.items_count === 1 ? 'event' : 'events' }}
            </p>
            <br v-if="generatedInvoice.email_sent || generatedInvoice.email_queued">
            <span v-if="generatedInvoice.email_sent || generatedInvoice.email_queued" class="GenerateInvoice-emailSent">
              ✓ Email sent to {{ generatedInvoice.email_sent_to || userEmail }}
            </span>
            <br v-if="generatedInvoice.pdf_url">
            <a v-if="generatedInvoice.pdf_url" :href="generatedInvoice.pdf_url" target="_blank" class="GenerateInvoice-pdfLink">
              {{ generatedInvoice.is_duplicate ? 'View existing invoice PDF' : 'Download PDF' }}
            </a>
            <br v-if="showSendEmailButton">
            <button
              v-if="showSendEmailButton"
              type="button"
              class="Button GenerateInvoice-sendEmail"
              :disabled="isSendingEmail"
              @click="sendInvoiceEmail"
            >
              <span v-if="isSendingEmail">Sending…</span>
              <span v-else>Send Email</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import cookieModel from '../../models/cookie';
import axios from "axios";
import { getDefaultInvoiceDateRange, getTodayLocalYMD } from '../../utils/date-local.js';
import errorModel from "../../models/error";
import toastModel from '../../models/toast';
import './_GenerateInvoice.scss';

export default {
  components: {},
  props: {
    user: {
      type: Object,
      required: true,
      default: function() {
        return {
          id: null,
          forename: '',
          surname: '',
          email: '',
          company: ''
        }
      }
    }
  },
  computed: {
    userDisplayName: function () {
      if (!this.user) return '';
      const parts = [];
      if (this.user.forename) parts.push(this.user.forename);
      if (this.user.surname) parts.push(this.user.surname);
      if (this.user.company) parts.push(`(${this.user.company})`);
      return parts.join(' ') || this.user.email || 'Unknown User';
    },
    userEmail: function () {
      return this.user ? this.user.email : '';
    },
    isDisabled: function () {
      return !this.form.user_id || 
             !this.form.date_from || 
             !this.form.date_to || 
             !!this.dateRangeError;
    },
    maxDate: function () {
      return getTodayLocalYMD();
    },
    dateRangeError: function () {
      if (!this.form.date_from || !this.form.date_to) {
        return null;
      }
      if (this.form.date_to < this.form.date_from) {
        return 'To date must be on or after from date';
      }
      return null;
    },
    hasTotals: function () {
      const inv = this.generatedInvoice;
      return inv && (inv.total_gross != null || inv.total_fees != null || inv.total_net != null);
    },
    showSendEmailButton: function () {
      if (!this.generatedInvoice || this.generatedInvoice.status !== 'draft') {
        return false;
      }
      return !this.generatedInvoice.email_queued &&
             !this.generatedInvoice.email_sent &&
             !this.generatedInvoice.date_sent;
    }
  },
  methods: {
    formatCurrency: function (value) {
      if (value == null || value === '') return '—';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (Number.isNaN(num)) return value;
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(num);
    },
    generateInvoice: function () {
      this.formSubmitted = true;
      
      // Validate form
      if (this.isDisabled) {
        this.asyncFormError = 'Please complete all required fields correctly';
        return;
      }

      const vm = this;
      vm.disableForm = true;
      vm.isSubmitting = true;
      vm.$set(vm, 'asyncFormError', '');
      vm.$set(vm, 'successMessage', '');
      vm.$set(vm, 'generatedInvoice', null);

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' +
        cookieModel.getCookie('ticketlab_access');

      // Prepare request data
      const postData = {
        user_id: vm.form.user_id,
        date_from: vm.form.date_from,
        date_to: vm.form.date_to,
        invoice_type: vm.form.invoice_type,
        auto_send: vm.form.auto_send || false
      };

      // Send request to API
      const url = vm.baseUrl + '/invoices/generate';
      
      vm.axiosInstance.post(url, postData)
        .then(function (response) {
          
          // Check if response indicates an error (even with 200 status)
          if (response.data && (response.data.error || response.data.status === 'error')) {
            const errorMessage = response.data.error || 
                                 response.data.message || 
                                 (response.data.errors && Object.values(response.data.errors).flat().join(', ')) ||
                                 'Failed to generate invoice';
            vm.$set(vm, 'asyncFormError', errorMessage);
            vm.$set(vm, 'successMessage', '');
            vm.$set(vm, 'generatedInvoice', null);
            console.error('Invoice generation error response:', response.data);
          }
          // Check if we have invoice data (response structure: {data: {...}, message: '...'})
          else if (response.data && response.data.data && response.data.data.invoice_id) {
            const d = response.data.data;
            vm.$set(vm, 'asyncFormError', '');
            vm.$set(vm, 'successMessage', response.data.message || 'Invoice generated successfully!');
            vm.$set(vm, 'generatedInvoice', {
              invoice_id: d.invoice_id,
              invoice_reference: d.invoice_reference,
              status: d.status,
              total_gross: d.total_gross != null ? d.total_gross : null,
              total_fees: d.total_fees != null ? d.total_fees : null,
              total_net: d.total_net != null ? d.total_net : null,
              items_count: d.items_count != null ? d.items_count : null,
              pdf_url: d.pdf_url_signed || d.signed_pdf_url || d.pdf_url || null,
              is_duplicate: !!d.is_duplicate,
              previous_invoice: d.previous_invoice || null,
              email_queued: !!d.email_queued,
              email_sent: !!(d.email_sent || d.email_queued),
              email_sent_to: d.email_sent_to || d.recipient_email || null,
              date_sent: d.date_sent || null
            });

            // Emit success event for parent component
            vm.$emit('success', response.data);

            const emailTo = d.email_sent_to || d.recipient_email || null;
            toastModel.show(
              emailTo
                ? `Invoice sent to ${emailTo}`
                : 'Invoice generated successfully'
            );

            // Reset form after short delay (optional - allows user to see success)
            setTimeout(() => {
              // Optionally reset form or keep it open for another invoice
            }, 3000);
          } else {
            // Handle case where response doesn't have expected structure
            const errorMessage = response.data?.message || 
                                 response.data?.error || 
                                 (response.data?.errors && Object.values(response.data.errors).flat().join(', ')) ||
                                 'Failed to generate invoice. Unexpected response format.';
            vm.$set(vm, 'asyncFormError', errorMessage);
            vm.$set(vm, 'successMessage', '');
            vm.$set(vm, 'generatedInvoice', null);
            console.error('Unexpected response format:', response.data);
          }
          
          vm.disableForm = false;
          vm.isSubmitting = false;
        })
        .catch(function (error) {
          console.error('Invoice generation error:', error);
          
          // Check for error in response data (API may return error field)
          let errorMessage = false;
          if (error.response && error.response.data) {
            if (error.response.data.error) {
              errorMessage = error.response.data.error;
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.data.reason) {
              errorMessage = error.response.data.reason;
            } else if (error.response.data.errors) {
              // Handle validation errors
              const errors = error.response.data.errors;
              if (typeof errors === 'object') {
                errorMessage = Object.values(errors).flat().join(', ');
              } else {
                errorMessage = errors.toString();
              }
            }
          }
          
          // Fall back to errorModel if no error message found yet
          if (!errorMessage) {
            errorMessage = errorModel.apiError(error, vm.$router);
          }
          
          // If still no error message, use a default
          if (!errorMessage) {
            errorMessage = 'Failed to generate invoice. Please try again.';
          }
          
          vm.$set(vm, 'asyncFormError', errorMessage);
          vm.$set(vm, 'successMessage', '');
          vm.$set(vm, 'generatedInvoice', null);
          vm.disableForm = false;
          vm.isSubmitting = false;
        });
    },
    sendInvoiceEmail: function () {
      const vm = this;
      if (!vm.generatedInvoice || !vm.generatedInvoice.invoice_id) {
        return;
      }

      vm.isSendingEmail = true;
      vm.$set(vm, 'asyncFormError', '');

      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' +
        cookieModel.getCookie('ticketlab_access');

      const url = vm.baseUrl + '/invoices/' + vm.generatedInvoice.invoice_id + '/send';

      return vm.axiosInstance.post(url, undefined, {})
        .then(function (response) {
          const data = response.data && response.data.data ? response.data.data : response.data;
          vm.$set(vm.generatedInvoice, 'status', data.status || 'sent');
          vm.$set(vm.generatedInvoice, 'date_sent', data.date_sent || null);
          vm.$set(vm.generatedInvoice, 'email_sent', data.email_queued || data.sent || false);
          vm.$set(vm.generatedInvoice, 'email_sent_to', data.recipient_email || null);
          vm.$set(vm.generatedInvoice, 'email_queued', data.email_queued || false);
          vm.$set(vm, 'successMessage', response.data.message || 'Invoice email queued successfully');
          const emailTo = data.recipient_email || vm.generatedInvoice?.email_sent_to || null;
          toastModel.show(emailTo ? `Invoice sent to ${emailTo}` : 'Invoice email sent');
        })
        .catch(function (error) {
          let errorMessage = false;
          if (error.response && error.response.data) {
            if (error.response.data.error) {
              errorMessage = error.response.data.error;
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.data.errors) {
              const errors = error.response.data.errors;
              errorMessage = typeof errors === 'object'
                ? Object.values(errors).flat().join(', ')
                : errors.toString();
            }
          }
          if (!errorMessage) {
            errorMessage = errorModel.apiError(error, vm.$router);
          }
          if (!errorMessage) {
            errorMessage = 'Failed to send invoice email. Please try again.';
          }
          vm.$set(vm, 'asyncFormError', errorMessage);
        })
        .finally(function () {
          vm.$set(vm, 'isSendingEmail', false);
        });
    }
  },
  data: function () {
    return {
      baseUrl: process.env.VUE_APP_API_URL,
      axiosInstance: axios.create({
        baseURL: this.baseUrl,
      }),
      form: {
        user_id: null,
        date_from: '',
        date_to: '',
        invoice_type: 'organiser',
        auto_send: false
      },
      formSubmitted: false,
      disableForm: false,
      isSubmitting: false,
      isSendingEmail: false,
      asyncFormError: '',
      successMessage: '',
      generatedInvoice: null
    }
  },
  mounted: function () {
    // Pre-fill user_id from prop
    if (this.user && this.user.id) {
      this.form.user_id = this.user.id;
    }
    
    // Monday–Wednesday of current week (local dates); end capped at today for API limits
    const range = getDefaultInvoiceDateRange();
    this.form.date_from = range.date_from;
    this.form.date_to = range.date_to;
  },
  watch: {
    // Update user_id when user prop changes
    'user.id': function (newUserId) {
      if (newUserId) {
        this.form.user_id = newUserId;
      }
    }
  }
}
</script>

